import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getTrackBackground, Range } from 'react-range';
import { useNavigate } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import { Button, Form, Image, Modal, OverlayTrigger, Tooltip as BootstrapToolTip } from 'react-bootstrap';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import Select from 'react-select';
import AsyncCreatableSelect from 'react-select/async-creatable';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

import { useAppDispatch } from '../../../store/hooks';
import SpinnerLoader from '../../common/SpinnerLoader';
import { backBtn } from '../../../assets/images/images';
import { NUMBERS } from '../../../resources/number-constants';
import { getIndustryType } from '../../../store/features/auth';
import { candidateSelectionGraphData, candidateSelectionMilesGraphData } from '../../../store/features/dashboard';
import { requestWithParam } from '../../../utility/apiClients';
import { APIS_METHOD } from '../../../resources/api-constants';
import { getStoredDistance, setStoredDistance } from '../../../utility/distancePrefs';
import { getStoredLocation, setStoredLocation } from '../../../utility/locationPrefs';
import CustomRangeSlider from '../../common/CustomRangeSlider';
import ZeroCandidatesModal from '../../modals/ZeroCandidatesModal';
import { extractListItemsFromHTML } from '../../../utility/helpers';
import { createCandidateSelectionChartData, createCandidateSelectionChartOptions } from '../../../utility/chartUtils';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip, // Chart.js Tooltip remains registered
  Legend,
  Filler
);

type JobTitleFilterEntry = {
  title: string;
  yearsInRoleMin: string;
  yearsInRoleMax: string;
  currentOrLast12Months: boolean;
};

type IndustryExperienceEntry = {
  industry: string;
  yearsMin: string;
  yearsMax: string;
  currentOrLast12Months: boolean;
};

type CompanyFilterEntry = {
  name: string;
  currentOrLast12Months: boolean;
};

const clampExpYears = (raw: number): number => Math.min(50, Math.max(0, raw));

/** Map 0–50 total years to 0–100 for dual-thumb track gradient (matches job/industry sliders). */
const totalYearsToTrackBackgroundValues = (minY: number, maxY: number): [number, number] => {
  const lo = clampExpYears(minY);
  const hi = clampExpYears(maxY);
  return [(lo / 50) * 100, (hi / 50) * 100];
};

const clamp0to100 = (raw: number): number => Math.min(100, Math.max(0, raw));

/** Parse years string for 0–100 sliders; empty → null (unspecified). */
const parseYears0to100 = (s: string): number | null => {
  const t = (s || '').trim();
  if (t === '') return null;
  const n = Number(t);
  if (!Number.isFinite(n)) return null;
  return clamp0to100(n);
};

/** Ordered min/max for API (0–100); empty strings treated as 0. */
const orderedYearRange = (minS: string, maxS: string): { lo: number; hi: number } => {
  const a = parseYears0to100(minS);
  const b = parseYears0to100(maxS);
  const u = a ?? 0;
  const v = b ?? 0;
  return u <= v ? { lo: u, hi: v } : { lo: v, hi: u };
};

const toTitleCase = (input: string) => {
  const s = (input || '').trim();
  if (!s) return s;
  return s
    .replace(/\s+/g, ' ')
    .split(' ')
    .map((word) => {
      const w = word.trim();
      if (!w) return w;
      if (w.length <= 3 && w === w.toUpperCase()) return w;
      const lower = w.toLowerCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
};

// `colors` must have length values.length + 1 (react-range getTrackBackground). Dual-thumb → 3 stops: inactive, accent, inactive.
const renderAdvancedYearsSliderTrack =
  (valuesForBackground: number[], colors: string[]) =>
  ({ props, children }: { props: any; children: React.ReactNode }) =>
    (
      <div
        {...props}
        className="advanced-years-track"
        style={{
          ...props.style,
          background: 'transparent',
        }}
      >
        <div
          className="advanced-years-track-line"
          aria-hidden
          style={{
            height: 2,
            flexShrink: 0,
            width: '100%',
            borderRadius: 999,
            pointerEvents: 'none',
            background: getTrackBackground({
              values: valuesForBackground,
              colors,
              min: 0,
              max: 100,
            }),
          }}
        />
        {children}
      </div>
    );

const coerceExpYear = (v: unknown): number | null => {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(String(v).replace(/[^\d.+-]/g, ''));
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

/** Min/max total years of experience from persisted advanced-filters JSON (string-safe). */
const experienceRangeFromParsed = (parsed: any): { min: number; max: number } => {
  const minRaw = coerceExpYear(parsed?.experience_min) ?? 0;
  const maxRaw =
    coerceExpYear(parsed?.experience_max) ??
    coerceExpYear(parsed?.experience_score) ??
    coerceExpYear(parsed?.total_years_experience) ??
    coerceExpYear(parsed?.years_experience) ??
    0;
  let min = clampExpYears(minRaw);
  let max = clampExpYears(maxRaw);
  if (min > max) {
    const t = min;
    min = max;
    max = t;
  }
  return { min, max };
};

/** Total years range from graph-generate `job_kw` (e.g. `experience: [3, 5]`). */
const experienceRangeFromJobKw = (jobKw: any): { min: number; max: number } | null => {
  if (!jobKw || typeof jobKw !== 'object') return null;
  const exp = jobKw.experience;
  if (Array.isArray(exp) && exp.length >= 2) {
    const a = coerceExpYear(exp[0]);
    const b = coerceExpYear(exp[1]);
    if (a === null && b === null) return null;
    let lo = clampExpYears(a ?? 0);
    let hi = clampExpYears(b ?? 0);
    if (lo > hi) {
      const t = lo;
      lo = hi;
      hi = t;
    }
    return { min: lo, max: hi };
  }
  const loKw = coerceExpYear(jobKw.min_experience);
  const hiKw = coerceExpYear(jobKw.max_experience);
  if (loKw === null && hiKw === null) return null;
  let lo = clampExpYears(loKw ?? 0);
  let hi = clampExpYears(hiKw ?? 0);
  if (lo > hi) {
    const t = lo;
    lo = hi;
    hi = t;
  }
  if (lo === 0 && hi === 0) return null;
  return { min: lo, max: hi };
};

/**
 * Builds the `filters` object for the candidate search API contract.
 * All year values are passed in years; the backend converts to months.
 */
const buildFilters = (
  jobTitles: JobTitleFilterEntry[],
  industryEntries: IndustryExperienceEntry[],
  companyEntries: CompanyFilterEntry[],
  experienceMin: number,
  experienceMax: number,
  toggles: {
    has_publications: boolean;
    has_volunteering: boolean;
    has_test_scores: boolean;
    has_projects: boolean;
  },
): Record<string, any> => {
  const filters: Record<string, any> = {};

  // 1) Total experience range (slider)
  if (experienceMin > 0 || experienceMax > 0) {
    filters.total_experience_min_years = experienceMin;
    filters.total_experience_max_years = experienceMax;
  }

  // 2) Job title filters (structured) — per-title current + optional years range (0–100)
  const structuredTitleFilters = jobTitles
    .filter((e) => e.title)
    .map((e) => {
      const out: Record<string, any> = {
        name: e.title,
        current_or_last_12_months: !!e.currentOrLast12Months,
      };
      const { lo, hi } = orderedYearRange(e.yearsInRoleMin, e.yearsInRoleMax);
      if (hi > 0) {
        out.years_max_years = hi;
        if (lo > 0) out.years_min_years = lo;
        out.years = hi;
      }
      return out;
    });
  if (structuredTitleFilters.length > 0) {
    filters.job_title_filters = structuredTitleFilters;
  }

  // 3) Company filters (structured) — per-company current flag
  const structuredCompanyFilters = companyEntries
    .filter((c) => c.name)
    .map((c) => ({
      name: c.name,
      ...(c.currentOrLast12Months ? { current_or_last_12_months: true } : {}),
    }));
  if (structuredCompanyFilters.length > 0) {
    filters.companies_worked_in = structuredCompanyFilters;
  }

  // 4) Role experience filters — entries with years range → AND match on role_experience keys
  const roleExpFilters = jobTitles
    .filter((e) => e.title && orderedYearRange(e.yearsInRoleMin, e.yearsInRoleMax).hi > 0)
    .map((e) => {
      const { lo, hi } = orderedYearRange(e.yearsInRoleMin, e.yearsInRoleMax);
      const row: Record<string, any> = { name: e.title, years: hi, years_max_years: hi };
      if (lo > 0) row.years_min_years = lo;
      return row;
    });
  if (roleExpFilters.length > 0) {
    filters.role_experience_filters = roleExpFilters;
  }

  // 5) Industry experience filters — AND match on industry_experience keys (years range 0–100)
  const industryExpFilters = industryEntries
    .filter((e) => e.industry)
    .map((e) => {
      const { lo, hi } = orderedYearRange(e.yearsMin, e.yearsMax);
      const row: Record<string, any> = {
        name: e.industry,
        ...(e.currentOrLast12Months ? { current_or_last_12_months: true } : {}),
      };
      if (hi > 0) {
        row.years_max_years = hi;
        if (lo > 0) row.years_min_years = lo;
        row.years = hi;
      }
      return row;
    });
  if (industryExpFilters.length > 0) {
    filters.industry_experience_filters = industryExpFilters;
  }

  // 6) Boolean toggles (always explicit)
  filters.has_publications = !!toggles.has_publications;
  filters.has_volunteering = !!toggles.has_volunteering;
  filters.has_test_scores = !!toggles.has_test_scores;
  filters.has_projects = !!toggles.has_projects;

  return filters;
};

const CandidateSelection = ({ jobId, setPercentageFitValue, aboutFor, jobDistance, loading, jobLocation, setCandidateLoading }: any) => {
  const darkThemeVar = 'theme-dark';
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [graphData, setGraphData] = useState<any>([]);
  const [sliderValue, setSliderValue] = useState<number>(0);
  const [selectedCandidates, setSelectedCandidates] = useState<number>(0);
  const [trackColors, setTrackColors] = useState<[string, string]>([
    '#3dcbb1',
    'rgba(41,41,41,0.1)',
  ]);
  // Three gradient stops for dual-thumb sliders (job/industry years + total experience).
  const [experienceRangeTrackColors, setExperienceRangeTrackColors] = useState<
    [string, string, string]
  >(['rgba(41,41,41,0.1)', '#3dcbb1', 'rgba(41,41,41,0.1)']);

  const [loadGraph, setLoadGraph] = useState(false);
  const [showZeroCandidatesModal, setShowZeroCandidatesModal] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<string>("");
  const [advancedLocations, setAdvancedLocations] = useState<string[]>([]);
  const [advancedLocationInput, setAdvancedLocationInput] = useState<string>("");

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showUnaddedFilterWarning, setShowUnaddedFilterWarning] = useState(false);
  /** True when the warning was opened from closing the filters modal (X / backdrop); shows "Discard and close". */
  const [unaddedFilterWarningFromCloseAttempt, setUnaddedFilterWarningFromCloseAttempt] =
    useState(false);
  const [advancedJobTitles, setAdvancedJobTitles] = useState<JobTitleFilterEntry[]>([]);
  const [advancedCompaniesWorkedIn, setAdvancedCompaniesWorkedIn] = useState<CompanyFilterEntry[]>([]);
  const [advancedJobTitleInput, setAdvancedJobTitleInput] = useState<string>("");
  const [advancedJobTitleYearsMin, setAdvancedJobTitleYearsMin] = useState<string>("");
  const [advancedJobTitleYearsMax, setAdvancedJobTitleYearsMax] = useState<string>("");
  const [advancedJobTitleCurrentOrLast12Months, setAdvancedJobTitleCurrentOrLast12Months] = useState<boolean>(true);
  const [advancedCompanyInput, setAdvancedCompanyInput] = useState<string>('');
  const [advancedCompanyCurrentOrLast12Months, setAdvancedCompanyCurrentOrLast12Months] = useState<boolean>(true);
  const [advancedIndustryExperienceEntries, setAdvancedIndustryExperienceEntries] = useState<IndustryExperienceEntry[]>([]);
  const [advancedIndustryOption, setAdvancedIndustryOption] = useState<any>(null);
  const [advancedIndustryYearsMin, setAdvancedIndustryYearsMin] = useState<string>("");
  const [advancedIndustryYearsMax, setAdvancedIndustryYearsMax] = useState<string>("");
  const [advancedIndustryCurrentOrLast12Months, setAdvancedIndustryCurrentOrLast12Months] = useState<boolean>(true);
  const [advancedExperienceMin, setAdvancedExperienceMin] = useState<number>(0);
  const [advancedExperienceMax, setAdvancedExperienceMax] = useState<number>(0);
  /** Last applied experience range (mirrors miles `currentDistance` → modal slider). */
  const [appliedExperienceMin, setAppliedExperienceMin] = useState<number>(0);
  const [appliedExperienceMax, setAppliedExperienceMax] = useState<number>(0);
  const [advancedExperienceMinInput, setAdvancedExperienceMinInput] = useState<string>("0");
  const [advancedExperienceMaxInput, setAdvancedExperienceMaxInput] = useState<string>("0");
  const [isExperienceMinFocused, setIsExperienceMinFocused] = useState<boolean>(false);
  const [isExperienceMaxFocused, setIsExperienceMaxFocused] = useState<boolean>(false);
  const [showJobTitleYearsTooltip, setShowJobTitleYearsTooltip] = useState<boolean>(false);
  const [showIndustryYearsTooltip, setShowIndustryYearsTooltip] = useState<boolean>(false);
  const [showTotalExperienceYearsTooltip, setShowTotalExperienceYearsTooltip] = useState<boolean>(false);
  const [industryTypeOptions, setIndustryTypeOptions] = useState<any[]>([]);
  const [advancedMiles, setAdvancedMiles] = useState<number | null>(null);
  const [advancedHasPublications, setAdvancedHasPublications] = useState<boolean>(false);
  const [advancedHasVolunteering, setAdvancedHasVolunteering] = useState<boolean>(false);
  const [advancedHasTestScores, setAdvancedHasTestScores] = useState<boolean>(false);
  const [advancedHasProjects, setAdvancedHasProjects] = useState<boolean>(false);

  const advancedFiltersKey = `job:${jobId}:advancedFilters`;

  const restoreAdvancedFiltersFromStorage = useCallback(() => {
    if (!jobId) return;
    try {
      const raw = localStorage.getItem(advancedFiltersKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const parsedJobTitles = Array.isArray(parsed?.job_title)
        ? parsed.job_title
        : (parsed?.job_title ? [parsed.job_title] : []);
      const parsedJobTitleFilters = Array.isArray(parsed?.job_title_filters)
        ? parsed.job_title_filters
        : [];
      if (parsedJobTitleFilters.length > 0) {
        setAdvancedJobTitles(
          parsedJobTitleFilters.map((item: any) => {
            const legacyMax =
              item?.years_in_role !== null && item?.years_in_role !== undefined
                ? String(item.years_in_role)
                : "";
            const minStr =
              item?.years_in_role_min !== null && item?.years_in_role_min !== undefined
                ? String(item.years_in_role_min)
                : "";
            const maxStr =
              item?.years_in_role_max !== null && item?.years_in_role_max !== undefined
                ? String(item.years_in_role_max)
                : "";
            return {
              title: String(item?.title || "").trim(),
              yearsInRoleMin: minStr,
              yearsInRoleMax: maxStr || legacyMax,
              currentOrLast12Months:
                item?.current_or_last_12_months !== undefined
                  ? !!item.current_or_last_12_months
                  : true,
            };
          }).filter((item: JobTitleFilterEntry) => item.title)
        );
      } else {
        setAdvancedJobTitles(
          parsedJobTitles.map((title: any) => ({
            title: String(title || "").trim(),
            yearsInRoleMin: "",
            yearsInRoleMax: "",
            currentOrLast12Months: true,
          })).filter((item: JobTitleFilterEntry) => item.title)
        );
      }
      const parsedCompanies =
        Array.isArray(parsed?.companies_worked_in)
          ? parsed.companies_worked_in
          : (parsed?.companies_worked_in ? [parsed.companies_worked_in] : []);
      const parsedCompanyFilters =
        Array.isArray(parsed?.companies_worked_in_filters) ? parsed.companies_worked_in_filters : [];
      if (parsedCompanyFilters.length > 0) {
        setAdvancedCompaniesWorkedIn(
          parsedCompanyFilters
            .map((c: any) => ({
              name: String(c?.name || c?.company || '').trim(),
              currentOrLast12Months: c?.current_or_last_12_months !== undefined ? !!c.current_or_last_12_months : true,
            }))
            .filter((c: CompanyFilterEntry) => c.name)
        );
      } else {
        setAdvancedCompaniesWorkedIn(
          parsedCompanies
            .map((name: any) => ({ name: String(name || '').trim(), currentOrLast12Months: true }))
            .filter((c: CompanyFilterEntry) => c.name)
        );
      }
      setAdvancedLocations(
        Array.isArray(parsed?.locations)
          ? parsed.locations
          : (parsed?.locations ? [parsed.locations] : [])
      );
      setAdvancedIndustryExperienceEntries(
        Array.isArray(parsed?.industry_experience_filters)
          ? parsed.industry_experience_filters
            .map((item: any) => {
              const legacyY =
                item?.years !== null && item?.years !== undefined ? String(item.years) : "";
              const minStr =
                item?.years_min !== null && item?.years_min !== undefined
                  ? String(item.years_min)
                  : "";
              const maxStr =
                item?.years_max !== null && item?.years_max !== undefined
                  ? String(item.years_max)
                  : "";
              return {
                industry: String(item?.industry || "").trim(),
                yearsMin: minStr,
                yearsMax: maxStr || legacyY,
                currentOrLast12Months:
                  item?.current_or_last_12_months !== undefined
                    ? !!item.current_or_last_12_months
                    : true,
              };
            })
            .filter((item: IndustryExperienceEntry) => item.industry)
          : []
      );
      const { min: expMin, max: expMax } = experienceRangeFromParsed(parsed);
      // Do not reset 0,0 from storage over graph-prefill; only restore when user saved a non-zero range.
      if (expMin > 0 || expMax > 0) {
        setAdvancedExperienceMin(expMin);
        setAdvancedExperienceMax(expMax);
        setAppliedExperienceMin(expMin);
        setAppliedExperienceMax(expMax);
      }
      setAdvancedMiles(
        parsed?.distance_in_miles !== null && parsed?.distance_in_miles !== undefined
          ? Number(parsed?.distance_in_miles)
          : null
      );
      setAdvancedHasPublications(!!parsed?.has_publications);
      setAdvancedHasVolunteering(!!parsed?.has_volunteering);
      setAdvancedHasTestScores(!!parsed?.has_test_scores);
      setAdvancedHasProjects(!!parsed?.has_projects);
    } catch {
      // ignore invalid storage
    }
  }, [jobId, advancedFiltersKey]);

  useEffect(() => {
    restoreAdvancedFiltersFromStorage();
  }, [restoreAdvancedFiltersFromStorage]);

  useEffect(() => {
    if (selectedLocation) {
      setAdvancedLocationInput(selectedLocation);
      setAdvancedLocations((prev) => (prev.includes(selectedLocation) ? prev : [...prev, selectedLocation]));
    } else {
      setAdvancedLocationInput("");
    }
  }, [selectedLocation]);

  useEffect(() => {
    if (!isExperienceMinFocused) {
      setAdvancedExperienceMinInput(advancedExperienceMin >= 50 ? "50+" : String(advancedExperienceMin));
    }
  }, [advancedExperienceMin, isExperienceMinFocused]);

  useEffect(() => {
    if (!isExperienceMaxFocused) {
      setAdvancedExperienceMaxInput(advancedExperienceMax >= 50 ? "50+" : String(advancedExperienceMax));
    }
  }, [advancedExperienceMax, isExperienceMaxFocused]);

  const addUniqueItem = (list: string[], value: string) => {
    const cleaned = (value || "").trim();
    if (!cleaned) return list;
    if (list.some((item) => item.toLowerCase() === cleaned.toLowerCase())) return list;
    return [...list, cleaned];
  };

  const addUniqueCompany = (list: CompanyFilterEntry[], entry: CompanyFilterEntry) => {
    const cleaned = (entry?.name || '').trim();
    if (!cleaned) return list;
    if (list.some((item) => item.name.toLowerCase() === cleaned.toLowerCase())) return list;
    return [...list, { name: cleaned, currentOrLast12Months: !!entry.currentOrLast12Months }];
  };

  const companyAutocompleteTimerRef = useRef<number | null>(null);
  const companyAutocompleteSeqRef = useRef(0);
  const loadCompanyOptions = useCallback((inputValue: string) => {
    const q = (inputValue || '').trim();
    const minChars = 2;
    const debounceMs = 350;

    // Reset pending debounce timer (if any).
    if (companyAutocompleteTimerRef.current) {
      window.clearTimeout(companyAutocompleteTimerRef.current);
      companyAutocompleteTimerRef.current = null;
    }

    // For empty/short input, don't call API.
    if (!q || q.length < minChars) {
      // Return no options until user types enough chars.
      return Promise.resolve([]);
    }

    const seq = ++companyAutocompleteSeqRef.current;

    return new Promise<any[]>((resolve) => {
      companyAutocompleteTimerRef.current = window.setTimeout(async () => {
        try {
          const res = await requestWithParam(
            APIS_METHOD.GET,
            '/job-candidate/company-autocomplete/',
            {},
            { search: q, limit: 50 },
            false
          );

          // Ignore stale responses (user typed again).
          if (seq !== companyAutocompleteSeqRef.current) return;

          const raw = res?.data;
          const list = Array.isArray(raw)
            ? raw
            : Array.isArray(raw?.data)
              ? raw.data
              : Array.isArray(raw?.data?.results)
                ? raw.data.results
                : Array.isArray(raw?.results)
                  ? raw.results
                  : [];
          const options = list
            .map((item: any) => {
              const name =
                typeof item === 'string'
                  ? item
                  : String(item?.text || item?.name || item?.id || item?.value || item?.label || '').trim();
              if (!name) return null;
              return { value: name, label: toTitleCase(name) };
            })
            .filter(Boolean);
          resolve(options as any[]);
        } catch {
          if (seq !== companyAutocompleteSeqRef.current) return;
          resolve([]);
        }
      }, debounceMs);
    });
  }, []);

  useEffect(() => {
    return () => {
      if (companyAutocompleteTimerRef.current) {
        window.clearTimeout(companyAutocompleteTimerRef.current);
      }
    };
  }, []);

  const jobTitleAutocompleteTimerRef = useRef<number | null>(null);
  const jobTitleAutocompleteSeqRef = useRef(0);
  const loadJobTitleOptions = useCallback((inputValue: string) => {
    const q = (inputValue || '').trim();
    const minChars = 2;
    const debounceMs = 350;

    if (jobTitleAutocompleteTimerRef.current) {
      window.clearTimeout(jobTitleAutocompleteTimerRef.current);
      jobTitleAutocompleteTimerRef.current = null;
    }

    if (!q || q.length < minChars) {
      return Promise.resolve([]);
    }

    const seq = ++jobTitleAutocompleteSeqRef.current;

    return new Promise<any[]>((resolve) => {
      jobTitleAutocompleteTimerRef.current = window.setTimeout(async () => {
        try {
          const res = await requestWithParam(
            APIS_METHOD.GET,
            '/job-candidate/job-title-autocomplete/',
            {},
            { search: q, limit: 30 },
            false
          );

          if (seq !== jobTitleAutocompleteSeqRef.current) return;

          const raw = res?.data;
          const body = raw?.data !== undefined && raw?.data !== null ? raw.data : raw;
          const list = Array.isArray(body?.results)
            ? body.results
            : Array.isArray(raw?.results)
              ? raw.results
              : Array.isArray(raw?.data)
                ? raw.data
                : [];
          const options = list
            .map((item: any) => {
              const name =
                typeof item === 'string'
                  ? item
                  : String(item?.text || item?.id || item?.name || item?.value || item?.label || '').trim();
              if (!name) return null;
              return { value: name, label: toTitleCase(name) };
            })
            .filter(Boolean);
          resolve(options as any[]);
        } catch {
          if (seq !== jobTitleAutocompleteSeqRef.current) return;
          resolve([]);
        }
      }, debounceMs);
    });
  }, []);

  useEffect(() => {
    return () => {
      if (jobTitleAutocompleteTimerRef.current) {
        window.clearTimeout(jobTitleAutocompleteTimerRef.current);
      }
    };
  }, []);

  const clampExperienceScore = clampExpYears;

  const addJobTitle = () => {
    const normalizedTitle = advancedJobTitleInput.trim();
    if (!normalizedTitle) return;
    setAdvancedJobTitles((prev) => {
      if (prev.some((item) => item.title.toLowerCase() === normalizedTitle.toLowerCase())) {
        return prev;
      }
      return [
        ...prev,
        {
          title: normalizedTitle,
          yearsInRoleMin: advancedJobTitleYearsMin.trim(),
          yearsInRoleMax: advancedJobTitleYearsMax.trim(),
          currentOrLast12Months: advancedJobTitleCurrentOrLast12Months,
        },
      ];
    });
    setAdvancedJobTitleInput("");
    setAdvancedJobTitleYearsMin("");
    setAdvancedJobTitleYearsMax("");
    setAdvancedJobTitleCurrentOrLast12Months(true);
  };

  const addCompanyFromInput = () => {
    const name = advancedCompanyInput.trim();
    if (!name) return;
    setAdvancedCompaniesWorkedIn((prev) =>
      addUniqueCompany(prev, {
        name,
        currentOrLast12Months: advancedCompanyCurrentOrLast12Months,
      })
    );
    setAdvancedCompanyCurrentOrLast12Months(true);
    setAdvancedCompanyInput('');
  };

  /** Single-value display for AsyncCreatableSelect — `value={null}` hides the label after picking from the menu. */
  const advancedJobTitleCreatableValue = useMemo(
    () =>
      advancedJobTitleInput.trim()
        ? { label: advancedJobTitleInput, value: advancedJobTitleInput }
        : null,
    [advancedJobTitleInput],
  );

  const advancedCompanyCreatableValue = useMemo(
    () =>
      advancedCompanyInput.trim()
        ? { label: advancedCompanyInput, value: advancedCompanyInput }
        : null,
    [advancedCompanyInput],
  );

  useEffect(() => {
    const fetchIndustryTypes = async () => {
      try {
        const industryRes: any = await dispatch(getIndustryType()).unwrap();
        const options = Array.isArray(industryRes?.data)
          ? industryRes.data.map(({ id, name }: any) => ({ value: id, label: name }))
          : [];
        setIndustryTypeOptions([{ value: null, label: 'None' }, ...options]);
      } catch (error) {
        console.info('Error in fetching industry types', error);
      }
    };
    fetchIndustryTypes();
  }, [dispatch]);

  const hasUnaddedFilterEntry = useCallback(() => {
    if (advancedJobTitleInput.trim() !== '') return true;
    if (advancedCompanyInput.trim() !== '') return true;
    if (advancedIndustryOption != null && advancedIndustryOption.value != null) return true;
    return false;
  }, [advancedJobTitleInput, advancedCompanyInput, advancedIndustryOption]);

  /** Which section(s) still need Add — drives warning title + copy. */
  const unaddedFilterScenario = useMemo(():
    | 'jobTitle'
    | 'industry'
    | 'company'
    | 'multiple'
    | null => {
    const pendingJobTitle = advancedJobTitleInput.trim() !== '';
    const pendingCompany = advancedCompanyInput.trim() !== '';
    const pendingIndustry =
      advancedIndustryOption != null && advancedIndustryOption.value != null;
    const count = [pendingJobTitle, pendingCompany, pendingIndustry].filter(Boolean).length;
    if (count === 0) return null;
    if (count > 1) return 'multiple';
    if (pendingJobTitle) return 'jobTitle';
    if (pendingCompany) return 'company';
    if (pendingIndustry) return 'industry';
    return null;
  }, [advancedJobTitleInput, advancedCompanyInput, advancedIndustryOption]);

  const unaddedFilterWarningTitle = useMemo(() => {
    switch (unaddedFilterScenario) {
      case 'jobTitle':
        return 'Job title not added';
      case 'company':
        return 'Company not added';
      case 'industry':
        return 'Industry not added';
      case 'multiple':
        return 'Filters not added';
      default:
        return 'Filter not added';
    }
  }, [unaddedFilterScenario]);

  const unaddedFilterWarningBody = useMemo(() => {
    switch (unaddedFilterScenario) {
      case 'jobTitle':
        return 'Click Add to save your job title, or clear the field.';
      case 'company':
        return 'Click Add to save the company, pick a suggestion, or clear the field.';
      case 'industry':
        return 'Click Add to save your industry, or clear the selection.';
      case 'multiple':
        return 'Click Add in each section to save, or clear those fields.';
      default:
        return 'Click Add to save, or clear the fields.';
    }
  }, [unaddedFilterScenario]);

  const discardUnaddedFilterFields = useCallback(() => {
    setAdvancedJobTitleInput('');
    setAdvancedJobTitleYearsMin('');
    setAdvancedJobTitleYearsMax('');
    setAdvancedJobTitleCurrentOrLast12Months(true);
    setAdvancedCompanyInput('');
    setAdvancedIndustryOption(null);
    setAdvancedIndustryYearsMin('');
    setAdvancedIndustryYearsMax('');
    setAdvancedIndustryCurrentOrLast12Months(true);
  }, []);

  const addIndustryExperience = () => {
    const industry = String(advancedIndustryOption?.label || "").trim();
    if (!industry) return;
    setAdvancedIndustryExperienceEntries((prev) => {
      if (prev.some((item) => item.industry.toLowerCase() === industry.toLowerCase())) {
        return prev;
      }
      return [
        ...prev,
        {
          industry,
          yearsMin: advancedIndustryYearsMin.trim(),
          yearsMax: advancedIndustryYearsMax.trim(),
          currentOrLast12Months: advancedIndustryCurrentOrLast12Months,
        },
      ];
    });
    setAdvancedIndustryOption(null);
    setAdvancedIndustryYearsMin("");
    setAdvancedIndustryYearsMax("");
    setAdvancedIndustryCurrentOrLast12Months(true);
  };

  const fetchGraphByLocation = (payload: Record<string, any>) => {
    setLoadGraph(true);
    setGraphData([]);

    dispatch(candidateSelectionMilesGraphData(payload))
      .unwrap()
      .then((res: any) => {
        const filteredRecords = getHighPercentageFitRecords(res?.data?.data || []);
        processGraphDataResponse(filteredRecords);
        setLoadGraph(false);
      })
      .catch((error: any) => {
        console.error('Error fetching graph details:', error);
        setLoadGraph(false);
      });
  };

  const handleApplyAdvancedFilters = () => {
    if (hasUnaddedFilterEntry()) {
      setUnaddedFilterWarningFromCloseAttempt(false);
      setShowUnaddedFilterWarning(true);
      return;
    }
    const appliedDistance = advancedMiles !== null ? advancedMiles : null;
    const normalizedCity = cleanLocationText((advancedLocations[0] || selectedLocation || ""));

    // --- Build location fields (multi-location support) ---
    const effectiveLocations = advancedLocations.length > 0
      ? advancedLocations
      : (normalizedCity ? [normalizedCity] : []);

    const locationFields: Record<string, any> = {};
    if (effectiveLocations.length > 1) {
      locationFields.cities = effectiveLocations;
    } else if (effectiveLocations.length === 1) {
      locationFields.city = effectiveLocations[0];
    }

    // --- Build filters object per API contract ---
    const filtersObj = buildFilters(
      advancedJobTitles,
      advancedIndustryExperienceEntries,
      advancedCompaniesWorkedIn,
      advancedExperienceMin,
      advancedExperienceMax,
      {
        has_publications: advancedHasPublications,
        has_volunteering: advancedHasVolunteering,
        has_test_scores: advancedHasTestScores,
        has_projects: advancedHasProjects,
      }
    );

    // --- Full API payload for the graph/distance endpoint ---
    const apiPayload: Record<string, any> = {
      job: jobId,
      distance_in_miles: appliedDistance,
      ...locationFields,
      ...(Object.keys(filtersObj).length > 0 ? { filters: filtersObj } : {}),
    };

    // --- Internal storage format for UI state restoration on revisit ---
    const storagePayload = {
      job_title: advancedJobTitles.map((item) => item.title),
      job_title_filters: advancedJobTitles.map((item) => {
        const { lo, hi } = orderedYearRange(item.yearsInRoleMin, item.yearsInRoleMax);
        return {
          title: item.title,
          years_in_role: hi > 0 ? hi : null,
          years_in_role_min: lo > 0 ? lo : null,
          years_in_role_max: hi > 0 ? hi : null,
          current_or_last_12_months: item.currentOrLast12Months,
        };
      }),
      companies_worked_in: advancedCompaniesWorkedIn.map((c) => c.name),
      companies_worked_in_filters: advancedCompaniesWorkedIn.map((c) => ({
        name: c.name,
        current_or_last_12_months: c.currentOrLast12Months,
      })),
      locations: advancedLocations,
      distance_in_miles: appliedDistance,
      experience_min: advancedExperienceMin,
      experience_max: advancedExperienceMax,
      years_experience: advancedExperienceMax,
      total_years_experience: advancedExperienceMax,
      experience_score: advancedExperienceMax,
      industry_experience_filters: advancedIndustryExperienceEntries.map((item) => {
        const { lo, hi } = orderedYearRange(item.yearsMin, item.yearsMax);
        return {
          industry: item.industry,
          years: hi > 0 ? hi : null,
          years_min: lo > 0 ? lo : null,
          years_max: hi > 0 ? hi : null,
          current_or_last_12_months: item.currentOrLast12Months,
        };
      }),
      has_publications: advancedHasPublications,
      has_volunteering: advancedHasVolunteering,
      has_test_scores: advancedHasTestScores,
      has_projects: advancedHasProjects,
    };

    setSelectedLocation(normalizedCity);
    setStoredLocation(jobId, normalizedCity);
    setCurrentDistance(appliedDistance);
    setStoredDistance(jobId, appliedDistance);
    setAppliedExperienceMin(advancedExperienceMin);
    setAppliedExperienceMax(advancedExperienceMax);

    try {
      localStorage.setItem(advancedFiltersKey, JSON.stringify(storagePayload));
    } catch {
      // ignore storage errors
    }
    fetchGraphByLocation(apiPayload);
    setShowAdvancedFilters(false);
  };

  const handleAdvancedFiltersModalHide = () => {
    if (hasUnaddedFilterEntry()) {
      setUnaddedFilterWarningFromCloseAttempt(true);
      setShowUnaddedFilterWarning(true);
      return;
    }
    setShowAdvancedFilters(false);
  };

  const dismissUnaddedFilterWarning = () => {
    setShowUnaddedFilterWarning(false);
    setUnaddedFilterWarningFromCloseAttempt(false);
  };

  /** Clears pending job title / company / industry fields and closes only the warning (filters modal stays open). */
  const discardUnaddedFieldsAndDismissWarning = () => {
    discardUnaddedFilterFields();
    dismissUnaddedFilterWarning();
  };

  const discardUnaddedAndCloseFiltersModal = () => {
    discardUnaddedFilterFields();
    dismissUnaddedFilterWarning();
    setShowAdvancedFilters(false);
  };

  const handleClearAdvancedFilters = () => {
    try {
      localStorage.removeItem(advancedFiltersKey);
    } catch {
      // ignore
    }
    setAdvancedJobTitles([]);
    setAdvancedCompaniesWorkedIn([]);
    setAdvancedJobTitleInput("");
    setAdvancedJobTitleYearsMin("");
    setAdvancedJobTitleYearsMax("");
    setAdvancedJobTitleCurrentOrLast12Months(true);
    setAdvancedCompanyInput('');
    setAdvancedCompanyCurrentOrLast12Months(true);
    setAdvancedIndustryExperienceEntries([]);
    setAdvancedIndustryOption(null);
    setAdvancedIndustryYearsMin("");
    setAdvancedIndustryYearsMax("");
    setAdvancedIndustryCurrentOrLast12Months(true);
    setAdvancedExperienceMin(0);
    setAdvancedExperienceMax(0);
    setAppliedExperienceMin(0);
    setAppliedExperienceMax(0);
    setAdvancedMiles(null);
    setAdvancedHasPublications(false);
    setAdvancedHasVolunteering(false);
    setAdvancedHasTestScores(false);
    setAdvancedHasProjects(false);
    setAdvancedLocations([]);
    setAdvancedLocationInput("");
  };
  // Initialize currentDistance with stored distance or jobDistance prop
  const [currentDistance, setCurrentDistance] = useState<number | null>(() => {
    const stored = getStoredDistance(jobId);
    return stored !== undefined ? stored : (jobDistance ?? null);
  });
  const googleMapAPIKey = `${process.env.REACT_APP_GOOGLE_MAP_API_KEY}`;

  useEffect(() => {
    if (showAdvancedFilters) {
      setAdvancedMiles(currentDistance ?? null);
      setAdvancedExperienceMin(appliedExperienceMin);
      setAdvancedExperienceMax(appliedExperienceMax);
    }
  }, [showAdvancedFilters, currentDistance, appliedExperienceMin, appliedExperienceMax]);

  const cleanLocationText = (text: string) => {
    if (!text) return text;
    // Remove leading labels like "Located in", "Location:", "Location -"
    let out = text.replace(/^\s*(located\s+in|location\s*[:-])\s*/i, '');
    // Collapse extra whitespace
    out = out.replace(/\s+/g, ' ').trim();
    return out;
  };

  // Function to check for the theme-dark class and update track colors accordingly
  const updateTrackColors = () => {
    const dark = document.body.classList.contains(darkThemeVar);
    // One palette for job/industry years, Miles, percent-fit, and total experience (outer segments).
    const accent = dark ? '#3DCBB1' : '#3dcbb1';
    const inactive = dark ? 'rgba(255,255,255,0.48)' : 'rgba(41,41,41,0.1)';
    setTrackColors([accent, inactive]);
    setExperienceRangeTrackColors([inactive, accent, inactive]);
  };

  const calculateMinMax = (data: Array<any>) => {
    let min = 0;
    let max = NUMBERS.HUNDRED;

    if (data.length > 0) {
      min = Math.min(...data.map((item) => item.percentage_fit));
      max = Math.max(...data.map((item) => item.percentage_fit));

      if (min === max) {
        max += NUMBERS.FIFTY;
      }
    }

    return { min, max };
  }

  // Prepare the chart data and options using utility functions
  const data = createCandidateSelectionChartData(graphData);
  const options = createCandidateSelectionChartOptions();

  // Helper function to filter records with percentage_fit <= 100
  const getHighPercentageFitRecords = (data: any) => {
    if (!Array.isArray(data)) {
      throw new Error("Input must be an array of objects");
    }
    return data.filter(record => record.percentage_fit <= 100);
  };

  // Helper function to find nearest value in graph data
  const findNearestValue = useCallback((targetValue: number, data: any[]) => {
    if (!data.length) return null;
    return data.reduce((prev: any, curr: any) =>
      Math.abs(curr.percentage_fit - targetValue) < Math.abs(prev.percentage_fit - targetValue) ? curr : prev
    );
  }, []);

  // Helper function to process and set graph data response
  const processGraphDataResponse = useCallback((filteredRecords: any[]) => {
    const sortedRecords = [...filteredRecords].sort((a, b) => a.percentage_fit - b.percentage_fit);
    setGraphData(sortedRecords);
    if (sortedRecords.length > 0) {
      setSliderValue(sortedRecords[0].percentage_fit);
      setPercentageFitValue(sortedRecords[0].percentage_fit);
    } else {
      setSliderValue(0);
      setPercentageFitValue(0);
    }
  }, [setPercentageFitValue]);

  const fetchGraphDetails = useCallback(async () => {
    try {
      setLoadGraph(true);
      // Decide whether to force regenerate based on a per-job flag set by previous screens
      let needsRegenerate = false;
      try {
        const storageKey = `job:${jobId}:needsRegenerate`;
        needsRegenerate = localStorage.getItem(storageKey) === 'true';
      } catch (e) {
        // ignore storage errors
      }

      const payload: any = needsRegenerate ? { job: jobId, force: true } : { job: jobId };
      const res = await dispatch(candidateSelectionGraphData(payload)).unwrap();
      const filteredRecords = getHighPercentageFitRecords(res?.data?.data || []);
      processGraphDataResponse(filteredRecords);

      const jobKw = res?.data?.job_kw;
      const fromGraph = experienceRangeFromJobKw(jobKw);
      let storedExp = { min: 0, max: 0 };
      try {
        const rawFilters = localStorage.getItem(`job:${jobId}:advancedFilters`);
        if (rawFilters) {
          const parsed = JSON.parse(rawFilters);
          storedExp = experienceRangeFromParsed(parsed);
        }
      } catch {
        // ignore
      }
      const noStoredExperienceRange = storedExp.min === 0 && storedExp.max === 0;
      if (fromGraph && noStoredExperienceRange) {
        setAppliedExperienceMin(fromGraph.min);
        setAppliedExperienceMax(fromGraph.max);
        setAdvancedExperienceMin(fromGraph.min);
        setAdvancedExperienceMax(fromGraph.max);
      }
      // Clear the regenerate flag after a successful forced refresh
      if (needsRegenerate) {
        try {
          const storageKey = `job:${jobId}:needsRegenerate`;
          localStorage.removeItem(storageKey);
        } catch (e) {
          // ignore storage errors
        }
      }
    } catch (error) {
      console.error('Error fetching graph details:', error);
    } finally {
      setLoadGraph(false);
      setHasFetched(true);
    }
  }, [dispatch, jobId, processGraphDataResponse]);

  const handleSliderChange = (values: number[]) => {
    if (!graphData.length) return;

    setSliderValue(values[0]);
    const nearestValue = findNearestValue(values[0], graphData);
    setPercentageFitValue(nearestValue?.percentage_fit || 0);
  };

  const navigateTo = (route: any) => {
    navigate(route);
  };

  useEffect(() => {
    if (!hasFetched || loadGraph) return;

    if (!graphData.length) {
      setSelectedCandidates(0);
      setShowZeroCandidatesModal(true);
      return;
    }

    const nearestValue = findNearestValue(sliderValue, graphData);
    setSelectedCandidates(nearestValue?.number_of_candidates || 0);
  }, [sliderValue, graphData, findNearestValue, hasFetched, loadGraph]);

  useEffect(() => {
    if (jobId) {
      fetchGraphDetails();
    }
  }, [jobId, fetchGraphDetails]);

  // Sync currentDistance when jobDistance prop changes, but only if no stored distance exists
  useEffect(() => {
    const stored = getStoredDistance(jobId);
    if (stored !== undefined) {
      setCurrentDistance(stored);
    } else if (jobDistance !== undefined) {
      setCurrentDistance(jobDistance);
    }
  }, [jobId, jobDistance]);

  useEffect(() => {
    updateTrackColors(); // Set initial color based on the current theme
    const observer = new MutationObserver(updateTrackColors);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (showAdvancedFilters) {
      updateTrackColors();
    }
  }, [showAdvancedFilters]);

  useEffect(() => {
    if (setCandidateLoading) {
      setCandidateLoading(loadGraph);
    }
  }, [loadGraph, setCandidateLoading]);

  useEffect(() => {
    return () => {
      setCandidateLoading?.(false);
    };
  }, [setCandidateLoading]);

  // Prefill Location: prefer stored value, else derive from job description
  useEffect(() => {
    // 1) Stored per-job location takes precedence
    const stored = getStoredLocation(jobId);
    if (stored) {
      setSelectedLocation(stored);
      return;
    }
    // 2) Fallback to job description location
    // Always try to extract location from jobLocation, even if it might be empty
    // This ensures location is populated regardless of distance setting
    if (jobLocation) {
      let city = '';
      try {
        const items = extractListItemsFromHTML(String(jobLocation));
        const cleaned = (items || []).map((i) => cleanLocationText(i)).filter(Boolean);
        const unique = Array.from(new Set(cleaned));
        city = unique[0] || '';
      } catch (e) {
        city = cleanLocationText(String(jobLocation) || '');
      }
      if (city) {
        setSelectedLocation(city);
      }
    }
  }, [jobId, jobLocation]);

  const extraPixels = () => {
    const totalCounts = Math.max(
      ...graphData.map((item: any) => item.number_of_candidates)
    );
    if (totalCounts < 99) {
      return 0;
    } else if (totalCounts > 99 && totalCounts < 999) {
      return 7;
    } else if (totalCounts > 999 && totalCounts < 9999) {
      return 16;
    } else if (totalCounts > 9999 && totalCounts < 99999) {
      return 21;
    } else if (totalCounts > 99999 && totalCounts < 999999) {
      return 28;
    } else if (totalCounts > 999999) {
      return 35;
    }
    return 0;
  };

  const extraMargin = () => {
    const totalCounts = Math.max(
      ...graphData.map((item: any) => item.number_of_candidates)
    );
    if (totalCounts < 99) {
      return 0;
    } else if (totalCounts > 99 && totalCounts < 999) {
      return 9;
    } else if (totalCounts > 999 && totalCounts < 9999) {
      return 0;
    }
    return 0;
  };


  return (
    <>
      <div className="outer-section">
        <OverlayTrigger
          placement="top"
          overlay={
            <BootstrapToolTip>
              Back
            </BootstrapToolTip>
          }
        >
          <div className="back-btn" onClick={() => navigateTo(`/job-description-summary?jobId=${jobId}&about=${aboutFor}`)}>
            <Image
              src={backBtn}
              height={25}
              width={25}
              alt="Back"
              className="back-btn-arrow"
            />
          </div>
        </OverlayTrigger>
      </div>
      <div className='candidate-selection-header'>
        <div className="main-page-title-container">
          <div className="preview-title">
            Select Percentage Fit <span>.</span>
          </div>
          <p className="preview-subtitle">
            Set miles, Select percentage fit & review matching candidate pool.
          </p>
        </div>

        <Modal
          show={showAdvancedFilters}
          onHide={handleAdvancedFiltersModalHide}
          centered
          className="advanced-filters-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title className="advanced-filters-modal-title">Filters</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <div className="advanced-filters-grid">
                <div className="advanced-filters-field advanced-location-filter-group">
                  <div className="advanced-location-filter-heading">Location Section</div>
                  <div className="advanced-location-filter-row">
                    <div className="advanced-location-filter-col">
                      <Form.Label>Location</Form.Label>
                      <GooglePlacesAutocomplete
                        apiKey={googleMapAPIKey}
                        selectProps={{
                          value: advancedLocationInput ? { label: advancedLocationInput, value: advancedLocationInput } as any : null,
                          onChange: (option: any) => {
                            const label = option?.label || option?.value?.description || '';
                            const city = cleanLocationText(label || '');
                            setAdvancedLocationInput(city);
                            setAdvancedLocations((prev) => addUniqueItem(prev, city));
                          },
                          placeholder: 'Type City Name',
                          noOptionsMessage: () => 'Type City Name',
                          loadingMessage: () => 'Searching…',
                          openMenuOnFocus: true,
                          className: 'google-select-box',
                          classNamePrefix: 'google-select',
                          menuPortalTarget: document.body,
                          menuPosition: 'fixed',
                          menuPlacement: 'auto',
                          styles: {
                            menuPortal: (base: any) => ({ ...base, zIndex: 2000 }),
                            menu: (base: any) => ({ ...base, zIndex: 2000 }),
                          },
                        }}
                        autocompletionRequest={{
                          types: ['(cities)'],
                          componentRestrictions: { country: ['us'] },
                        }}
                      />
                      {advancedLocations.length > 0 && (
                        <div className="advanced-selected-pills">
                          {advancedLocations.map((loc) => (
                            <button
                              type="button"
                              key={`loc-${loc}`}
                              className="advanced-pill"
                              onClick={() => setAdvancedLocations((prev) => prev.filter((item) => item !== loc))}
                              title="Remove filter"
                            >
                              {loc} <span className="pill-x">x</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="advanced-location-filter-col">
                      <Form.Label>Miles</Form.Label>
                      <div className="advanced-filters-mile-slider">
                        <CustomRangeSlider
                          initialValue={advancedMiles}
                          onChange={(value: number | null) => setAdvancedMiles(value)}
                          trackColors={trackColors}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="advanced-filters-field advanced-job-title-filter-group">
                  <div className="advanced-job-title-filter-heading">Job Title Section</div>
                  <div className="advanced-job-title-filter-row">
                    <div className="advanced-job-title-filter-col">
                      <Form.Label>Job Title</Form.Label>
                      <AsyncCreatableSelect
                        classNamePrefix="google-select"
                        className="google-select-box"
                        cacheOptions
                        defaultOptions={false}
                        loadOptions={loadJobTitleOptions as any}
                        // Do not pass `inputValue`: controlling it to the selected label keeps react-select's
                        // internal input non-empty, so it hides SingleValue and sets the input opacity to 0
                        // (empty-looking field with clear × until blur). Sync typing via onInputChange only.
                        onInputChange={(v, meta) => {
                          if (meta.action === 'input-change') {
                            setAdvancedJobTitleInput(v);
                          }
                        }}
                        blurInputOnSelect
                        onChange={(option: any) => {
                          if (!option) {
                            setAdvancedJobTitleInput('');
                            return;
                          }
                          const v = String(option.label ?? option.value ?? '').trim();
                          if (v) setAdvancedJobTitleInput(v);
                        }}
                        value={advancedJobTitleCreatableValue}
                        isClearable
                        placeholder="Type Job Title"
                        openMenuOnFocus
                        noOptionsMessage={({ inputValue }) =>
                          (inputValue || '').trim().length < 2 ? 'Type Job Title' : 'No matches'
                        }
                        loadingMessage={() => 'Searching…'}
                        formatCreateLabel={(input) => `Use "${input}"`}
                        createOptionPosition="first"
                        allowCreateWhileLoading
                        menuPortalTarget={document.body}
                        menuPlacement="auto"
                        menuPosition="fixed"
                        styles={{
                          menuPortal: (base: any) => ({ ...base, zIndex: 2000 }),
                          menu: (base: any) => ({ ...base, zIndex: 2000 }),
                        }}
                      />
                    </div>
                    <div className="advanced-job-title-filter-col">
                      <Form.Label>Years in Role (range)</Form.Label>
                      <div className="advanced-years-slider-field">
                        <div className="advanced-years-slider-row">
                          <div className="advanced-years-slider-wrap">
                            <Range
                              step={1}
                              min={0}
                              max={100}
                              values={(() => {
                                const { lo, hi } = orderedYearRange(
                                  advancedJobTitleYearsMin,
                                  advancedJobTitleYearsMax,
                                );
                                return [lo, hi];
                              })()}
                              onChange={(values) => {
                                const a = clamp0to100(values[0]);
                                const b = clamp0to100(values[1]);
                                const lo = Math.min(a, b);
                                const hi = Math.max(a, b);
                                setAdvancedJobTitleYearsMin(lo === 0 ? '' : String(lo));
                                setAdvancedJobTitleYearsMax(hi === 0 ? '' : String(hi));
                              }}
                              renderTrack={renderAdvancedYearsSliderTrack(
                                (() => {
                                  const { lo, hi } = orderedYearRange(
                                    advancedJobTitleYearsMin,
                                    advancedJobTitleYearsMax,
                                  );
                                  return [lo, hi];
                                })(),
                                experienceRangeTrackColors,
                              )}
                              renderThumb={({ props, index, isDragged }) => {
                                const { lo, hi } = orderedYearRange(
                                  advancedJobTitleYearsMin,
                                  advancedJobTitleYearsMax,
                                );
                                const tip = index === 0 ? lo : hi;
                                return (
                                  <OverlayTrigger
                                    placement="bottom"
                                    overlay={
                                      <BootstrapToolTip id="years-role-tooltip" className="bs-tooltip">
                                        {tip}
                                      </BootstrapToolTip>
                                    }
                                    show={showJobTitleYearsTooltip || isDragged}
                                  >
                                    <div
                                      {...props}
                                      className={`advanced-years-thumb ${isDragged ? 'is-dragged' : ''}`}
                                      onMouseEnter={() => setShowJobTitleYearsTooltip(true)}
                                      onMouseLeave={() => setShowJobTitleYearsTooltip(false)}
                                    />
                                  </OverlayTrigger>
                                );
                              }}
                            />
                            <div className="advanced-years-ends">
                              <span>0</span>
                              <span>100</span>
                            </div>
                          </div>
                          <div className="advanced-years-range-inputs">
                            <Form.Control
                              type="number"
                              min={0}
                              max={100}
                              value={advancedJobTitleYearsMin}
                              onChange={(e) => {
                                const raw = e.target.value;
                                if (raw.trim() === '') {
                                  setAdvancedJobTitleYearsMin('');
                                  return;
                                }
                                const next = clamp0to100(Number(raw));
                                setAdvancedJobTitleYearsMin(String(next));
                                const hi = parseYears0to100(advancedJobTitleYearsMax) ?? 0;
                                if (hi > 0 && next > hi) setAdvancedJobTitleYearsMax(String(next));
                              }}
                              placeholder="Min"
                              className="advanced-years-input"
                            />
                            <span className="advanced-years-range-sep" aria-hidden>
                              –
                            </span>
                            <Form.Control
                              type="number"
                              min={0}
                              max={100}
                              value={advancedJobTitleYearsMax}
                              onChange={(e) => {
                                const raw = e.target.value;
                                if (raw.trim() === '') {
                                  setAdvancedJobTitleYearsMax('');
                                  return;
                                }
                                const next = clamp0to100(Number(raw));
                                setAdvancedJobTitleYearsMax(String(next));
                                const lo = parseYears0to100(advancedJobTitleYearsMin) ?? 0;
                                if (next > 0 && next < lo) setAdvancedJobTitleYearsMin(String(next));
                              }}
                              placeholder="Max"
                              className="advanced-years-input"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <Form.Check
                    type="checkbox"
                    id="adv-job-title-current-or-last-12"
                    label="Current or within last 12 months"
                    checked={advancedJobTitleCurrentOrLast12Months}
                    onChange={(e) => setAdvancedJobTitleCurrentOrLast12Months(e.target.checked)}
                    className="advanced-job-title-checkbox"
                  />
                  <Button
                    type="button"
                    variant="primary"
                    className="advanced-job-title-add-btn"
                    onClick={addJobTitle}
                    disabled={!advancedJobTitleInput.trim()}
                  >
                    Add
                  </Button>
                  {advancedJobTitles.length > 0 && (
                    <div className="advanced-selected-pills">
                      {advancedJobTitles.map((titleEntry) => (
                        <button
                          type="button"
                          key={`job-${titleEntry.title}`}
                          className="advanced-pill"
                          onClick={() =>
                            setAdvancedJobTitles((prev) =>
                              prev.filter((item) => item.title !== titleEntry.title)
                            )
                          }
                          title={`Job Title: ${titleEntry.title}
Years in Role: ${(() => {
                            const { lo, hi } = orderedYearRange(
                              titleEntry.yearsInRoleMin,
                              titleEntry.yearsInRoleMax,
                            );
                            if (hi <= 0) return 'Not specified';
                            if (lo <= 0) return `0–${hi}`;
                            return `${lo}–${hi}`;
                          })()}
Current/Last 12 Months: ${titleEntry.currentOrLast12Months ? "Yes" : "No"}`}
                        >
                          {titleEntry.title}{' '}
                          {(() => {
                            const { lo, hi } = orderedYearRange(
                              titleEntry.yearsInRoleMin,
                              titleEntry.yearsInRoleMax,
                            );
                            return hi > 0 ? (
                              <span className="advanced-pill-years">
                                ({lo > 0 ? `${lo}–` : ''}
                                {hi}y)
                              </span>
                            ) : null;
                          })()}{' '}
                          <span className="pill-x">x</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="advanced-filters-field advanced-company-history-group">
                  <div className="advanced-company-history-heading">Company History Section</div>
                  <div className="advanced-company-history-row">
                    <div className="advanced-company-history-col">
                      <Form.Label>Companies Worked In</Form.Label>
                      <AsyncCreatableSelect
                        classNamePrefix="google-select"
                        className="google-select-box"
                        cacheOptions
                        defaultOptions={false}
                        loadOptions={loadCompanyOptions as any}
                        onInputChange={(v, meta) => {
                          if (meta.action === 'input-change') {
                            setAdvancedCompanyInput(v);
                          }
                        }}
                        blurInputOnSelect
                        onChange={(option: any) => {
                          if (!option) {
                            setAdvancedCompanyInput('');
                            return;
                          }
                          const name = String(option.label ?? option.value ?? '').trim();
                          if (name) setAdvancedCompanyInput(name);
                        }}
                        value={advancedCompanyCreatableValue}
                        isClearable
                        placeholder="Type Company Name"
                        openMenuOnFocus
                        noOptionsMessage={({ inputValue }) =>
                          (inputValue || '').trim().length < 2 ? 'Type Company Name' : 'No matches'
                        }
                        loadingMessage={() => 'Searching…'}
                        formatCreateLabel={(input) => `Use "${input}"`}
                        createOptionPosition="first"
                        allowCreateWhileLoading
                        menuPortalTarget={document.body}
                        menuPlacement="auto"
                        menuPosition="fixed"
                        styles={{
                          menuPortal: (base: any) => ({ ...base, zIndex: 2000 }),
                          menu: (base: any) => ({ ...base, zIndex: 2000 }),
                        }}
                      />
                      <Button
                        type="button"
                        variant="primary"
                        className="advanced-job-title-add-btn"
                        onClick={addCompanyFromInput}
                        disabled={!advancedCompanyInput.trim()}
                      >
                        Add
                      </Button>
                    </div>
                    <div className="advanced-company-history-col advanced-company-history-slider-col">
                      <div className="advanced-experience-score-subheading">
                        Total Years of Work Experience
                      </div>
                      <div className="advanced-years-slider-field">
                        <div className="advanced-years-slider-row">
                          <div className="advanced-years-slider-wrap">
                            <Range
                              step={1}
                              min={0}
                              max={50}
                              values={[advancedExperienceMin, advancedExperienceMax]}
                              onChange={(values) => {
                                const a = clampExperienceScore(values[0]);
                                const b = clampExperienceScore(values[1]);
                                setAdvancedExperienceMin(Math.min(a, b));
                                setAdvancedExperienceMax(Math.max(a, b));
                              }}
                              renderTrack={renderAdvancedYearsSliderTrack(
                                totalYearsToTrackBackgroundValues(
                                  advancedExperienceMin,
                                  advancedExperienceMax,
                                ),
                                experienceRangeTrackColors,
                              )}
                              renderThumb={({ props, index, isDragged }) => {
                                const tip =
                                  index === 0 ? advancedExperienceMin : advancedExperienceMax;
                                const tipLabel = tip >= 50 ? "50+" : String(tip);
                                return (
                                  <OverlayTrigger
                                    placement="bottom"
                                    overlay={
                                      <BootstrapToolTip
                                        id="years-total-exp-tooltip"
                                        className="bs-tooltip"
                                      >
                                        {tipLabel}
                                      </BootstrapToolTip>
                                    }
                                    show={showTotalExperienceYearsTooltip || isDragged}
                                  >
                                    <div
                                      {...props}
                                      className={`advanced-years-thumb ${isDragged ? 'is-dragged' : ''}`}
                                      onMouseEnter={() => setShowTotalExperienceYearsTooltip(true)}
                                      onMouseLeave={() => setShowTotalExperienceYearsTooltip(false)}
                                    />
                                  </OverlayTrigger>
                                );
                              }}
                            />
                            <div className="advanced-years-ends">
                              <span>0</span>
                              <span>50+</span>
                            </div>
                          </div>
                          <div className="advanced-years-range-inputs advanced-experience-total-years-inputs">
                            <Form.Control
                              type="text"
                              inputMode="numeric"
                              placeholder="Min"
                              value={
                                isExperienceMinFocused
                                  ? advancedExperienceMinInput
                                  : advancedExperienceMin >= 50 ? "50+" : String(advancedExperienceMin)
                              }
                              onFocus={() => {
                                setIsExperienceMinFocused(true);
                                setAdvancedExperienceMinInput(String(advancedExperienceMin));
                              }}
                              onBlur={() => setIsExperienceMinFocused(false)}
                              onChange={(e) => {
                                const raw = e.target.value;
                                setAdvancedExperienceMinInput(raw);
                                const digits = raw.replace(/[^\d]/g, "");
                                if (digits === "") { setAdvancedExperienceMin(0); return; }
                                const next = clampExperienceScore(Number(digits));
                                if (Number.isNaN(next)) return;
                                setAdvancedExperienceMin(next);
                                if (next > advancedExperienceMax) setAdvancedExperienceMax(next);
                              }}
                              className="advanced-years-input"
                            />
                            <span className="advanced-years-range-sep" aria-hidden>
                              –
                            </span>
                            <Form.Control
                              type="text"
                              inputMode="numeric"
                              placeholder="Max"
                              value={
                                isExperienceMaxFocused
                                  ? advancedExperienceMaxInput
                                  : advancedExperienceMax >= 50 ? "50+" : String(advancedExperienceMax)
                              }
                              onFocus={() => {
                                setIsExperienceMaxFocused(true);
                                setAdvancedExperienceMaxInput(String(advancedExperienceMax));
                              }}
                              onBlur={() => setIsExperienceMaxFocused(false)}
                              onChange={(e) => {
                                const raw = e.target.value;
                                setAdvancedExperienceMaxInput(raw);
                                const digits = raw.replace(/[^\d]/g, "");
                                if (digits === "") { setAdvancedExperienceMax(0); return; }
                                const next = clampExperienceScore(Number(digits));
                                if (Number.isNaN(next)) return;
                                setAdvancedExperienceMax(next);
                                if (next < advancedExperienceMin) setAdvancedExperienceMin(next);
                              }}
                              className="advanced-years-input"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="advanced-company-history-follow-row">
                    <Form.Check
                      type="checkbox"
                      id="adv-company-current-or-last-12"
                      label="Current or within last 12 months"
                      checked={advancedCompanyCurrentOrLast12Months}
                      onChange={(e) => setAdvancedCompanyCurrentOrLast12Months(e.target.checked)}
                      className="advanced-job-title-checkbox"
                    />
                    <div className="advanced-filters-toggles-grid advanced-company-history-toggles advanced-company-history-toggles-inline">
                      <Form.Check
                        type="checkbox"
                        id="adv-publications"
                        label="Publications"
                        checked={advancedHasPublications}
                        onChange={(e) => setAdvancedHasPublications(e.target.checked)}
                      />
                      <Form.Check
                        type="checkbox"
                        id="adv-volunteering"
                        label="Volunteering"
                        checked={advancedHasVolunteering}
                        onChange={(e) => setAdvancedHasVolunteering(e.target.checked)}
                      />
                      <Form.Check
                        type="checkbox"
                        id="adv-test-scores"
                        label="Test Scores"
                        checked={advancedHasTestScores}
                        onChange={(e) => setAdvancedHasTestScores(e.target.checked)}
                      />
                      <Form.Check
                        type="checkbox"
                        id="adv-projects"
                        label="Projects"
                        checked={advancedHasProjects}
                        onChange={(e) => setAdvancedHasProjects(e.target.checked)}
                      />
                    </div>
                  </div>
                  {advancedCompaniesWorkedIn.length > 0 && (
                    <div className="advanced-selected-pills">
                      {advancedCompaniesWorkedIn.map((company) => (
                        <button
                          type="button"
                          key={`company-${company.name}`}
                          className="advanced-pill"
                          onClick={() => setAdvancedCompaniesWorkedIn((prev) => prev.filter((item) => item.name !== company.name))}
                          title={`Company: ${company.name}\nCurrent/Last 12 Months: ${company.currentOrLast12Months ? "Yes" : "No"}`}
                        >
                          {toTitleCase(company.name)}
                          {company.currentOrLast12Months ? " (current)" : ""} <span className="pill-x">x</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="advanced-filters-field advanced-industry-filter-group">
                  <div className="advanced-industry-filter-heading">Industry Section</div>
                  <div className="advanced-experience-filter-row advanced-experience-industry-row">
                    <div className="advanced-experience-filter-col">
                      <Form.Label>Industry</Form.Label>
                      <Select
                        classNamePrefix="google-select"
                        className="google-select-box"
                        options={industryTypeOptions}
                        value={advancedIndustryOption}
                        onChange={(option: any) => setAdvancedIndustryOption(option?.value ? option : null)}
                        placeholder="Select industry"
                        menuPortalTarget={document.body}
                        menuPlacement="auto"
                        menuPosition="fixed"
                        styles={{
                          menuPortal: (base: any) => ({ ...base, zIndex: 2000 }),
                          menu: (base: any) => ({ ...base, zIndex: 2000 }),
                        }}
                      />
                    </div>
                    <div className="advanced-experience-filter-col">
                      <Form.Label>Years in Industry (range)</Form.Label>
                      <div className="advanced-years-slider-field">
                        <div className="advanced-years-slider-row">
                          <div className="advanced-years-slider-wrap">
                            <Range
                              step={1}
                              min={0}
                              max={100}
                              values={(() => {
                                const { lo, hi } = orderedYearRange(
                                  advancedIndustryYearsMin,
                                  advancedIndustryYearsMax,
                                );
                                return [lo, hi];
                              })()}
                              onChange={(values) => {
                                const a = clamp0to100(values[0]);
                                const b = clamp0to100(values[1]);
                                const lo = Math.min(a, b);
                                const hi = Math.max(a, b);
                                setAdvancedIndustryYearsMin(lo === 0 ? '' : String(lo));
                                setAdvancedIndustryYearsMax(hi === 0 ? '' : String(hi));
                              }}
                              renderTrack={renderAdvancedYearsSliderTrack(
                                (() => {
                                  const { lo, hi } = orderedYearRange(
                                    advancedIndustryYearsMin,
                                    advancedIndustryYearsMax,
                                  );
                                  return [lo, hi];
                                })(),
                                experienceRangeTrackColors,
                              )}
                              renderThumb={({ props, index, isDragged }) => {
                                const { lo, hi } = orderedYearRange(
                                  advancedIndustryYearsMin,
                                  advancedIndustryYearsMax,
                                );
                                const tip = index === 0 ? lo : hi;
                                return (
                                  <OverlayTrigger
                                    placement="bottom"
                                    overlay={
                                      <BootstrapToolTip id="years-industry-tooltip" className="bs-tooltip">
                                        {tip}
                                      </BootstrapToolTip>
                                    }
                                    show={showIndustryYearsTooltip || isDragged}
                                  >
                                    <div
                                      {...props}
                                      className={`advanced-years-thumb ${isDragged ? 'is-dragged' : ''}`}
                                      onMouseEnter={() => setShowIndustryYearsTooltip(true)}
                                      onMouseLeave={() => setShowIndustryYearsTooltip(false)}
                                    />
                                  </OverlayTrigger>
                                );
                              }}
                            />
                            <div className="advanced-years-ends">
                              <span>0</span>
                              <span>100</span>
                            </div>
                          </div>
                          <div className="advanced-years-range-inputs">
                            <Form.Control
                              type="number"
                              min={0}
                              max={100}
                              value={advancedIndustryYearsMin}
                              onChange={(e) => {
                                const raw = e.target.value;
                                if (raw.trim() === '') {
                                  setAdvancedIndustryYearsMin('');
                                  return;
                                }
                                const next = clamp0to100(Number(raw));
                                setAdvancedIndustryYearsMin(String(next));
                                const hi = parseYears0to100(advancedIndustryYearsMax) ?? 0;
                                if (hi > 0 && next > hi) setAdvancedIndustryYearsMax(String(next));
                              }}
                              placeholder="Min"
                              className="advanced-years-input"
                            />
                            <span className="advanced-years-range-sep" aria-hidden>
                              –
                            </span>
                            <Form.Control
                              type="number"
                              min={0}
                              max={100}
                              value={advancedIndustryYearsMax}
                              onChange={(e) => {
                                const raw = e.target.value;
                                if (raw.trim() === '') {
                                  setAdvancedIndustryYearsMax('');
                                  return;
                                }
                                const next = clamp0to100(Number(raw));
                                setAdvancedIndustryYearsMax(String(next));
                                const lo = parseYears0to100(advancedIndustryYearsMin) ?? 0;
                                if (next > 0 && next < lo) setAdvancedIndustryYearsMin(String(next));
                              }}
                              placeholder="Max"
                              className="advanced-years-input"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <Form.Check
                    type="checkbox"
                    id="adv-industry-current-or-last-12"
                    label="Current or within last 12 months"
                    checked={advancedIndustryCurrentOrLast12Months}
                    onChange={(e) => setAdvancedIndustryCurrentOrLast12Months(e.target.checked)}
                    className="advanced-job-title-checkbox"
                  />
                  <Button
                    type="button"
                    variant="primary"
                    className="advanced-experience-add-btn"
                    onClick={addIndustryExperience}
                    disabled={!advancedIndustryOption?.value}
                  >
                    Add
                  </Button>
                  {advancedIndustryExperienceEntries.length > 0 && (
                    <div className="advanced-selected-pills">
                      {advancedIndustryExperienceEntries.map((entry) => (
                        <button
                          type="button"
                          key={`industry-${entry.industry}`}
                          className="advanced-pill"
                          onClick={() =>
                            setAdvancedIndustryExperienceEntries((prev) =>
                              prev.filter(
                                (item) =>
                                  item.industry !== entry.industry
                              )
                            )
                          }
                          title={`Industry: ${entry.industry}
Years in Industry: ${(() => {
                            const { lo, hi } = orderedYearRange(entry.yearsMin, entry.yearsMax);
                            if (hi <= 0) return 'Not specified';
                            if (lo <= 0) return `0–${hi}`;
                            return `${lo}–${hi}`;
                          })()}
Current/Last 12 Months: ${entry.currentOrLast12Months ? "Yes" : "No"}`}
                        >
                          {entry.industry}
                          {(() => {
                            const { lo, hi } = orderedYearRange(entry.yearsMin, entry.yearsMax);
                            return hi > 0 ? (
                              <span className="advanced-pill-years">
                                {' '}
                                ({lo > 0 ? `${lo}–` : ''}
                                {hi}y)
                              </span>
                            ) : null;
                          })()}
                          {entry.currentOrLast12Months ? ' (current)' : ''}{' '}
                          <span className="pill-x">x</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Form>
          </Modal.Body>
          <Modal.Footer className="advanced-filters-footer">
            <Button
              variant="link"
              className="advanced-filters-clear-btn"
              onClick={handleClearAdvancedFilters}
            >
              Clear
            </Button>
            <Button
              variant="primary"
              className="advanced-filters-footer-btn"
              onClick={handleApplyAdvancedFilters}
            >
              Apply
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={showUnaddedFilterWarning}
          onHide={dismissUnaddedFilterWarning}
          centered
          className="advanced-filters-unadded-warning-modal"
          backdropClassName="advanced-filters-unadded-warning-backdrop"
        >
          <Modal.Header closeButton className="advanced-filters-unadded-warning-header">
            <Modal.Title className="advanced-filters-unadded-warning-title">{unaddedFilterWarningTitle}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="advanced-filters-unadded-warning-body">{unaddedFilterWarningBody}</Modal.Body>
          <Modal.Footer className="advanced-filters-unadded-warning-footer">
            <Button
              variant="link"
              className="advanced-filters-unadded-warning-discard"
              onClick={
                unaddedFilterWarningFromCloseAttempt
                  ? discardUnaddedAndCloseFiltersModal
                  : discardUnaddedFieldsAndDismissWarning
              }
            >
              {unaddedFilterWarningFromCloseAttempt ? 'Discard and close' : 'Discard'}
            </Button>
            <Button
              variant="primary"
              className="advanced-filters-footer-btn advanced-filters-unadded-warning-ok"
              onClick={dismissUnaddedFilterWarning}
            >
              OK
            </Button>
          </Modal.Footer>
        </Modal>

        <div className="main-page-title-container-slider">
          <div className="location-content">
            <Button
              variant="light"
              className="advanced-filters-btn-location"
              disabled={!!loading}
              onClick={() => {
                // Ensure modal opens with hydrated state (avoid flicker/delay).
                restoreAdvancedFiltersFromStorage();
                setShowAdvancedFilters(true);
              }}
            >
                <span className="advanced-filters-btn-inline-icon" aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2 3H14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    <circle cx="5" cy="3" r="2" fill="currentColor" />
                    <path d="M2 8H14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    <circle cx="10" cy="8" r="2" fill="currentColor" />
                    <path d="M2 13H14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    <circle cx="6" cy="13" r="2" fill="currentColor" />
                  </svg>
                </span>
                Filters
            </Button>
          </div>
        </div>
      </div>
      <div className='graph-mobile-structure'>
        {!loadGraph && (
          <>
            <div className="graph-container-wrapper" >
              <div className='slider-container' style={{ width: '100%' }}>
                <div className="slider-text" style={{ width: `${(87 + extraPixels())}px` }}>Percent Fit</div>
                <div className="slider-inner" style={{ marginRight: `${(17 + extraMargin())}px` }}>
                  <Range
                    step={0.1}
                    min={calculateMinMax(graphData).min}
                    max={calculateMinMax(graphData).max}
                    // min={graphData.length > 0 ? Math.min(...graphData.map((item: any) => item.percentage_fit)) : 0}
                    // max={graphData.length > 0 ? Math.max(...graphData.map((item: any) => item.percentage_fit)) : NUMBERS.HUNDRED}
                    values={[sliderValue]}
                    onChange={handleSliderChange}
                    onFinalChange={(values) => {
                      if (!graphData.length) return;
                      const nearestValue = findNearestValue(values[0], graphData);
                      if ((nearestValue?.number_of_candidates || 0) === 0) {
                        setShowZeroCandidatesModal(true);
                      }
                    }}
                    renderTrack={({ props, children }) => (
                      <div
                        {...props}
                        className="track"
                        style={{
                          ...props.style,
                          background: getTrackBackground({
                            values: [sliderValue],
                            colors: trackColors,
                            min: calculateMinMax(graphData).min,
                            max: calculateMinMax(graphData).max,
                          }),
                        }}
                      >
                        {children}
                      </div>
                    )}
                    renderThumb={({ props }) => (
                      <div {...props} className="thumb">
                        <div className="value-box">{sliderValue}%</div>
                      </div>
                    )}
                  />
                </div>
              </div>
              <div style={{ width: '100%', height: 300 }}>
                <Line data={data} options={options} />
              </div>
            </div>
            <div className="slider-container">
              <div className="slider-value">
                <strong>{selectedCandidates}</strong> {`Candidate${selectedCandidates > 1 ? 's' : ''}`}
              </div>
            </div>
          </>
        )}

        {loadGraph && (
          <div className='candidate-loader-lazy'>
            <SpinnerLoader />
            Just a moment! We're loading your results.
          </div>
        )}
      </div>

      <ZeroCandidatesModal
        show={showZeroCandidatesModal}
        onHide={() => setShowZeroCandidatesModal(false)}
      />
    </>
  );
};

export default CandidateSelection;
